/**
 * @prettier
 */

import { useCallback, useState, useEffect } from 'react';
import _values from 'lodash/values';
import _fromPairs from 'lodash/fromPairs';
import { MOBILE_MAX } from 'lib/responsive';
// import { CARD_TYPES } from 'cards/constants/CardTypes';
import {
  isSingleSampleLimitExhausted,
  getCardSuiteDetails,
  getTemplate,
  uploadPhoto,
} from '../api';
import ApiService from '../../../../../../util/api';
import generateUUID from '../../../../../../lib/uuid';
import {
  lookupDefaultVariation,
  getIsSampleCustomizable,
  templateHasCustomImage,
} from '../helpers';

export function useSingleSampleLimitExhausted(cardType) {
  const globalKey = 'ZOLA_INVITATIONS_SINGLE_SAMPLE_MODAL_SAMPLE_LIMIT_EXHAUSTED_PROMISE';

  const [isExhausted, setIsExhausted] = useState(false);

  const updateIsExhausted = useCallback(
    exhausted => isExhausted !== exhausted && setIsExhausted(exhausted),
    [setIsExhausted]
  );

  const fetchIsExhausted = useCallback(() => {
    window[globalKey] = isSingleSampleLimitExhausted(cardType);
    return window[globalKey].then(updateIsExhausted);
  }, [cardType, updateIsExhausted]);

  /**
   * Cache whether the sample limit is exhausted in a global variable
   */
  useEffect(() => {
    if (cardType) {
      if (window[globalKey] && window[globalKey].then) {
        window[globalKey].then(updateIsExhausted);
      } else {
        fetchIsExhausted();
      }
    }
  });

  return [isExhausted, fetchIsExhausted];
}

export function useDebounce(callback, delay, dependencies) {
  useEffect(() => {
    const handler = setTimeout(callback, delay);

    return () => {
      clearTimeout(handler);
    };
  }, dependencies);
}

export function usePreviewTemplate(
  cardSuiteDetails,
  form,
  sampleNotCustomizable,
  isMagnet,
  isMulti,
  cardSuiteDetailsSet,
  activePreview
) {
  const [template, setTemplate] = useState(null);

  const weddingDetails = form && form.weddingDetails;
  const variation =
    form &&
    (isMulti ? form.customizations.variationSet[activePreview] : form.customizations.variation);
  const color =
    form && (isMulti ? form.customizations.colorSet[activePreview] : form.customizations.color);

  useEffect(() => {
    getTemplate(
      isMulti ? cardSuiteDetailsSet && cardSuiteDetailsSet[activePreview] : cardSuiteDetails,
      variation,
      color,
      weddingDetails,
      sampleNotCustomizable,
      isMagnet
    ).then(setTemplate);
  }, [
    variation,
    color,
    sampleNotCustomizable,
    isMagnet,
    cardSuiteDetails,
    isMulti,
    activePreview,
    Object.keys(cardSuiteDetailsSet).join('_'),
    ...(weddingDetails ? _values(weddingDetails) : []),
  ]);

  return template;
}

export function useDefaultTemplateSet(
  cardSuiteUUIDList,
  cardSuiteDetailsSet,
  isMulti,
  multiPreviewState,
  setMultiPreviewState
) {
  const [defaultTemplateSet, setDefaultTemplateSet] = useState(null);

  useEffect(() => {
    if (!isMulti) {
      if (!multiPreviewState.initialized) {
        setMultiPreviewState({
          ...multiPreviewState,
          initialized: true,
        });
      }
      setDefaultTemplateSet({});
      return;
    }

    if (
      !cardSuiteDetailsSet ||
      !cardSuiteUUIDList ||
      Object.keys(cardSuiteDetailsSet).length !== cardSuiteUUIDList.length
    ) {
      return;
    }

    const fetchDefaultTemplates = Promise.all(
      cardSuiteUUIDList.map(uuid => {
        const cardSuiteDetails = cardSuiteDetailsSet[uuid];

        if (!cardSuiteDetails) {
          return [];
        }

        const defaultOptions = cardSuiteDetails.mediums.paper.default_variation_options;

        const fetchTemplate = getTemplate(
          cardSuiteDetails,
          lookupDefaultVariation(cardSuiteDetailsSet[uuid]),
          defaultOptions.color,
          null,
          !getIsSampleCustomizable(cardSuiteDetails)
        );

        return fetchTemplate.then(template => [uuid, template]);
      })
    );

    fetchDefaultTemplates.then(pairs => {
      setDefaultTemplateSet(_fromPairs(pairs));

      const foundPair = pairs.find(
        ([uuid, template]) =>
          templateHasCustomImage(template) && getIsSampleCustomizable(cardSuiteDetailsSet[uuid])
      );

      const [cardUUIDWithCustomizableImage] = foundPair || [];

      if (!multiPreviewState.initialized) {
        setMultiPreviewState({
          ...multiPreviewState,
          initialized: true,
          activePreview: cardUUIDWithCustomizableImage || multiPreviewState.activePreview,
        });
      }
    });
  }, [cardSuiteUUIDList, cardSuiteDetailsSet, isMulti, multiPreviewState, setMultiPreviewState]);

  return defaultTemplateSet;
}

export function useCardSuiteDetails(cardSuiteUUID, isAdminView, isMulti) {
  const [cardSuiteDetails, setCardSuiteDetails] = useState(null);

  useEffect(() => {
    if (!isMulti && cardSuiteUUID) {
      getCardSuiteDetails(cardSuiteUUID, isAdminView).then(setCardSuiteDetails);
    }
  }, [cardSuiteUUID, isAdminView, isMulti]);

  return cardSuiteDetails;
}

export function useCardSuiteDetailsSet(cardSuiteUUIDList, isAdminView, isMulti) {
  const [cardSuiteDetailsSet, setCardSuiteDetailsSet] = useState({});

  useEffect(() => {
    if (isMulti && cardSuiteUUIDList) {
      Promise.all(
        cardSuiteUUIDList.map(cardSuiteUUID =>
          getCardSuiteDetails(cardSuiteUUID, isAdminView).then(suiteDetails => [
            cardSuiteUUID,
            suiteDetails,
          ])
        )
      )
        .then(_fromPairs)
        .then(setCardSuiteDetailsSet);
    }
  }, [(cardSuiteUUIDList || []).join('_'), isAdminView, isMulti]);

  return cardSuiteDetailsSet;
}

export function usePhotoObjectURL(photo) {
  const [photoURL, setPhotoURL] = useState();

  useEffect(() => {
    if (photo && photo.file) {
      setPhotoURL(URL.createObjectURL(photo.displayedBlob));
    }

    return () => photoURL && URL.revokeObjectURL(photoURL);
  }, [photo]);

  return photoURL;
}

const MAX_PHOTO_CACHE_ENTRIES = 10e3;

export function usePhotoCache() {
  const sessionStorageKey = 'ZOLA_INVITATIONS_SINGLE_SAMPLE_MODAL_PHOTO_CACHE_STORE';

  const [cache, setCache] = useState(null);

  useEffect(() => {
    const recoveredCacheJSON = sessionStorage.getItem(sessionStorageKey) || '{}';

    let recoveredCache = {};

    try {
      recoveredCache = JSON.parse(recoveredCacheJSON);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn(`Cannot load photo cache from sessionStorage at: ${sessionStorageKey}`);
    }

    if (Object.keys(recoveredCache).length > MAX_PHOTO_CACHE_ENTRIES) {
      setCache({});
    }

    setCache(recoveredCache);
  }, []);

  const updateCache = newCache => {
    sessionStorage.setItem(sessionStorageKey, JSON.stringify(newCache));
    setCache(newCache);
  };

  const photoUploadHandler = (photo, crop) => {
    const key = `${photo.id}-${crop.x}-${crop.y}-${crop.width}-${crop.height}`;

    if (cache && cache[key]) {
      return Promise.resolve(cache[key]);
    }

    return uploadPhoto(photo, crop).then(photoUUID => {
      updateCache({ ...cache, [key]: photoUUID });

      return photoUUID;
    });
  };

  return photoUploadHandler;
}

export const usePhotoUploadHandler = (
  updateForm,
  minDimensions = { width: 1200, height: 1200 },
  isMulti = false,
  activePreview = null,
  cardSuiteUUIDList,
  defaultTemplateSet,
  onSuccess = () => {}
) =>
  useCallback(
    file => {
      if (!file) {
        return;
      }

      const url = URL.createObjectURL(file);
      const imgEl = document.createElement('img');

      imgEl.src = url;

      imgEl.onload = () => {
        const { width, height } = imgEl;

        const size = { width, height };

        const fileMB = file.size / 1024 / 1024;

        const valid = width >= minDimensions.width && height >= minDimensions.height && fileMB < 10;

        const filledSize = Math.max(width, height);

        const canvasEl = document.createElement('canvas');

        canvasEl.width = filledSize;
        canvasEl.height = filledSize;

        const canvasCtx = canvasEl.getContext('2d');

        const drawX = Math.floor((filledSize - width) / 2);
        const drawY = Math.floor((filledSize - height) / 2);

        canvasCtx.fillStyle = 'white';
        canvasCtx.fillRect(0, 0, filledSize, filledSize);
        canvasCtx.drawImage(imgEl, drawX, drawY);

        const saveBlob = displayedBlob => {
          const newPhoto = { id: generateUUID(), file, displayedBlob, valid, size, filledSize };
          if (!isMulti) {
            updateForm({ customizations: { photo: newPhoto } });
          }

          if (isMulti) {
            if (activePreview) {
              updateForm({ customizations: { photoSet: { [activePreview]: newPhoto } } });
            } else {
              updateForm({
                customizations: {
                  photoSet:
                    cardSuiteUUIDList &&
                    _fromPairs(
                      cardSuiteUUIDList
                        .filter(uuid => templateHasCustomImage(defaultTemplateSet[uuid]))
                        .map(uuid => [uuid, newPhoto])
                    ),
                },
              });
            }
          }

          if (valid) {
            onSuccess();
          }

          URL.revokeObjectURL(file);
        };

        if (canvasEl.msToBlob) {
          setTimeout(() => saveBlob(canvasEl.msToBlob()), 100);
        } else {
          canvasEl.toBlob(saveBlob);
        }
      };
    },
    [updateForm, minDimensions, isMulti, activePreview, cardSuiteUUIDList, onSuccess]
  );

export function useSpecialTypeSampleView(sampleSku) {
  const [skuData, setSkuData] = useState();

  useEffect(() => {
    if (!sampleSku) {
      setSkuData();
      return;
    }

    ApiService.post('/web-api/v1/products/find', { sku_ids: [sampleSku] }).then(data => {
      const newSkuData = skuData || {};

      if (
        data &&
        data[0] &&
        data[0].status &&
        data[0].product_look_views &&
        data[0].product_look_views[0] &&
        data[0].product_look_views[0].sku_previews &&
        data[0].product_look_views[0].sku_previews[0]
      ) {
        const preview = data[0].product_look_views[0].sku_previews[0];

        newSkuData.preview = preview;
      }

      if (
        data &&
        data[0] &&
        data[0].status &&
        data[0].product_look_views &&
        data[0].product_look_views[0] &&
        data[0].product_look_views[0].images &&
        data[0].product_look_views[0].images[0] &&
        data[0].product_look_views[0].images[0].aspect_ratios
      ) {
        const images = data[0].product_look_views[0].images[0].aspect_ratios;
        const imageKey = images['3x4'] ? '3x4' : '1x1';
        const image = images[imageKey] && images[imageKey].medium;

        newSkuData.image = image;
      }

      setSkuData(newSkuData);
    });
  }, [sampleSku, skuData]);

  return skuData;
}

export function useModalCloseCallback() {
  return useCallback(({ modalOptions, setModalOptions, setModalContext }) => {
    if (modalOptions.preventClose) {
      setModalContext({ showExitOverlay: true });
      setModalOptions({ hideClose: false, preventClose: false });
    }
  }, []);
}

export function useMobileCheck() {
  const [isMobile, setIsMobile] = useState(false);
  const matchMedia = () =>
    typeof window !== 'undefined' && window.matchMedia(`(max-width: ${MOBILE_MAX}px)`);

  useEffect(() => {
    const mediaQuery = matchMedia();

    const listener = media => setIsMobile(media.matches);

    mediaQuery.addListener(listener);

    return () => mediaQuery.removeListener(listener);
  }, [setIsMobile]);

  return isMobile;
}

export { default as useMostPopularSamplesFromFamily } from './useMostPopularSamplesFromFamily';
